import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import ProjectInputFilesCard from "./ProjectInputFilesCard";
import UploadFilePage from "./UploadFilePage";
import LinkedInputFilesCard from "./JobLinkedInputFilesCard";
import OutputFilesCard from "./JobOutputFilesCard";
import ProjectOutputFilesCard from "./ProjectOutputFilesCard";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";
import { AuthContext } from "../../../../../context/AuthContext";
import { isFreelance } from "../../../../../utils/constants/accountType";

const FilesPage = () => {
  const { id: projectId } = useParams();
  const { user } = useContext(AuthContext);
  const freelance = isFreelance(user);

  const [projectInputFiles, setProjectInputFiles] = useState([]);
  const [jobInputFiles, setJobInputFiles] = useState([]);
  const [jobOutputFiles, setJobOutputFiles] = useState([]);
  const [projectOutputFiles, setProjectOutputFiles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchProjectInputFiles = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const res = await api.get(`/project-input-files?project_id=${projectId}`, { withCredentials: true });
      setProjectInputFiles(res.data.data || []);
    } catch (error) {
      console.error("Error fetching project input files:", error);
      toast.error("Failed to fetch project input files");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobInputFiles = async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/job-input-files?project_id=${projectId}`, { withCredentials: true });
      setJobInputFiles(res.data.data || []);
    } catch (error) {
      console.error("Error fetching job input files:", error);
    }
  };

  const fetchJobOutputFiles = async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/job-output-files?project_id=${projectId}`, { withCredentials: true });
      setJobOutputFiles(res.data.data || []);
    } catch (error) {
      console.error("Error fetching job output files:", error);
    }
  };

  const fetchProjectOutputFiles = async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/project-output-files?project_id=${projectId}`, { withCredentials: true });
      setProjectOutputFiles(res.data.data || []);
    } catch (error) {
      console.error("Error fetching project output files:", error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectInputFiles();
      fetchProjectOutputFiles();
      if (!freelance) {
        fetchJobInputFiles();
        fetchJobOutputFiles();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, freelance]);

  const handleDelete = async (id) => {
    if (!await confirmDialog("Are you sure you want to delete this file?")) return;
    try {
      await api.delete(`/project-input-files/${id}`, { withCredentials: true });
      setProjectInputFiles((prev) => prev.filter((f) => f.id !== id));
      await fetchJobInputFiles();
      toast.success("File deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Failed to delete file");
    }
  };

  return (
    <div className="space-y-5">
      <ProjectInputFilesCard
        projectId={projectId}
        files={projectInputFiles}
        loading={loading}
        onDelete={handleDelete}
        onUpload={() => setShowUploadModal(true)}
      />

      {/* Linked (job) input/output files - enterprise only. */}
      {!freelance && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5">
            <LinkedInputFilesCard
              projectId={projectId}
              files={jobInputFiles}
            />
          </div>
          <div className="lg:col-span-7">
            <OutputFilesCard
              projectId={projectId}
              files={jobOutputFiles}
              onRefresh={() => {
                fetchJobOutputFiles();
                fetchProjectOutputFiles();
              }}
            />
          </div>
        </div>
      )}

      <ProjectOutputFilesCard
        projectId={projectId}
        files={projectOutputFiles}
        onRefresh={fetchProjectOutputFiles}
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadFilePage
          projectId={projectId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchProjectInputFiles();
          }}
        />
      )}
    </div>
  );
};

export default FilesPage;